import { useCallback, useMemo } from 'react';
import { SmartSendItemApiModel, ValidationType } from '~/lib/api/smartsend/types';
import ItemContents from './ItemContents';
import ItemMarketplaceDetails from './ItemMarketplaceDetails';
import ItemRecipient from './ItemRecipient';
import ItemService from './ItemService';
import ItemTotal from './ItemTotal';
import { EditType } from './EditType';
import { SmartSendItemSelect } from '../SmartSendItemSelect';
import { P2GGrid, P2GLinkGroup } from '@parcel2go/core-ui';
import { P2GBox } from '@parcel2go/core-ui';
import { ItemCustoms } from './ItemCustoms';
import { ItemActionType } from './SetMarketplaceAction';
import { RequiresCustoms, RequiresTaxIdsButNotCustoms } from '../../../lib/helpers/SmartSendValidation';
import SmartSendItemActiveDrop from './SmartSendItemActiveDrop';
import ItemCollectionDate from './ItemCollectionDate';
import { EventSections } from '../../../lib/events/EventLogger';
import ItemErrorSummary from './ItemErrorSummary';
import P2GContentDropDown from '../../P2GContentDropDown';
import { P2GLink } from '@parcel2go/core-ui';
import SmartSendItemRulesMenu from './SmartSendItemRulesMenu';
import { IsWindsor } from '~/apps/smartsend/lib/helpers/IsWindsor';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { SettingsCache } from '~/lib/consts/settings-cache';
import ClosestPickUpDropShop from './ClosestPickUpShop';

interface props {
  item: SmartSendItemApiModel;
  toggleSelect: (val: boolean) => void;
  removeTag: (tag: string) => void;
  saveTags: (tags: string[]) => void;
  edit: (type: EditType) => void;
  action: (type: ItemActionType) => void;
  saveForLater: (select: boolean) => void;
  deleteItem: () => void;
  book: () => void;
  setDate: (d: Date) => void;
  isReturn: boolean;
}

const errors = (match: string[], errors: ValidationType[]): string[] =>
  errors.filter((x) => !!match.find((m) => x.indexOf(m) === 0));

export default function SmartSendItemActive({
  edit,
  item,
  removeTag,
  saveTags,
  action,
  saveForLater,
  deleteItem,
  book,
  toggleSelect,
  isReturn,
  setDate
}: props) {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const itemErrors = useMemo(() => errors(['ADDRESS-'], item.State.validation.Errors), [item.State.validation.Errors]);

  const productErrors = useMemo(
    () => errors(['ITEM-', 'PRODUCT-'], item.State.validation.Errors),
    [item.State.validation.Errors]
  );

  const onEditCustoms = useCallback(() => {
    edit?.('CUSTOMS');
  }, [edit]);

  const onEdit = useCallback(
    (type: EditType) => {
      edit?.(type);
    },
    [edit]
  );

  const onAction = useCallback(
    (type: ItemActionType) => {
      action?.(type);
    },
    [action]
  );

  const ready = item.State.validation?.Validated;
  const requiresCustomsForItem = RequiresCustoms(item.State.validation);
  const requiresTaxIdsForItem = !requiresCustomsForItem && RequiresTaxIdsButNotCustoms(item.State.validation);

  return (
    <div className={ready ? 'p2g-smartsend-item-sold-ready' : 'p2g-smartsend-item-sold-notready'}>
      <P2GBox p={1}>
        <SmartSendItemSelect selected={item.State.selected} toggle={toggleSelect}>
          <P2GGrid container spacing={2}>
            <P2GGrid item sm={6} md={4}>
              <ItemContents edit={onEdit} item={item} errors={productErrors} variant="body2" />
            </P2GGrid>
            <P2GGrid item sm={6} md={3}>
              <ItemRecipient
                edit={onEdit}
                customer={isReturn ? item.Sender : item.Recipient}
                errors={itemErrors}
                variant="body2"
              />
            </P2GGrid>
            <ItemErrorSummary edit={onEdit} variant="body2" item={item} sm={12} md={5} component="Grid">
              <P2GGrid item sm={12} md={5}>
                <ItemService
                  showAsAlert={true}
                  itemId={item.Id}
                  dims={item.PackageDimensions}
                  variant="body2"
                  edit={onEdit}
                  book={book}
                  senderIsoCode={item.Sender?.CountryIsoCode}
                  recipientIsoCode={item.Recipient?.CountryIsoCode}
                  service={item.State.validation?.QuoteState?.Quote?.Service || item.Service}
                  children={
                    <P2GBox>
                      <P2GBox>
                        <P2GLinkGroup
                          links={[
                            <ItemTotal
                              key="total"
                              item={item}
                              quote={item.State.validation?.QuoteState?.Quote}
                              variant="body2"
                            />,
                            requiresCustomsForItem && (
                              <ItemCustoms
                                key="customs"
                                variant="body2"
                                edit={onEditCustoms}
                                label={
                                  IsWindsor({
                                    Sender: item.Sender,
                                    Recipient: item.Recipient
                                  } as SmartSendItemApiModel)
                                    ? t('ADDITIONAL_INFROMATION_TITLE', { defaultValue: 'Additional Information' })
                                    : null
                                }
                              />
                            ),
                            requiresTaxIdsForItem && (
                              <ItemCustoms
                                key="taxids"
                                variant="body2"
                                edit={onEditCustoms}
                                label={t('TAXT_IDS_LINK', { defaultValue: 'Tax Ids' })}
                              />
                            ),
                            book && (
                              <P2GLink key="book" onClick={book}>
                                {t('BOOK_BUTTON', { defaultValue: 'Book' })}
                              </P2GLink>
                            )
                          ]}
                        />
                      </P2GBox>

                      <ItemCollectionDate
                        hideHeader={false}
                        variant={'body2'}
                        dates={item.State.validation?.QuoteState?.Dates}
                        selected={item.PreferredCollectionDate}
                        setDate={setDate}
                      />
                    </P2GBox>
                  }
                />
              </P2GGrid>
            </ItemErrorSummary>
          </P2GGrid>
        </SmartSendItemSelect>
      </P2GBox>
      <ItemMarketplaceDetails
        section={EventSections.SmartSend}
        action={onAction}
        item={item}
        removeTag={removeTag}
        saveTags={saveTags}
        edit={onEdit}
        rules={SettingsCache.settings.Features.includes('InlineRules') && <SmartSendItemRulesMenu item={item} />}
        drop={
          <P2GContentDropDown
            control={
              <P2GLink variant="body2">
                <Trans t={t} i18nKey="QUICK_ACTIONS_LINK" defaults="Quick<br />Actions" />
              </P2GLink>
            }
          >
            <SmartSendItemActiveDrop
              item={item}
              edit={onEdit}
              action={onAction}
              saveForLater={saveForLater}
              deleteItem={deleteItem}
              book={book}
              canBook={item.State.validation?.Validated}
              direction="column"
            />
          </P2GContentDropDown>
        }
      />
      <ClosestPickUpDropShop item={item} wrapper={(x) => <P2GBox p={2}>{x}</P2GBox>} isXs={false} />
    </div>
  );
}
